import { useTranslation } from "react-i18next";
import type { ProgressSegment } from "@/engine/wizardUi";

interface WizardProgressProps {
  segments: ProgressSegment[];
  current: number;
  total: number;
  onNavigate: (segmentId: string) => void;
}

export function WizardProgress({ segments, current, total, onNavigate }: WizardProgressProps) {
  const { t } = useTranslation();

  return (
    <div className="pt-8 pb-4 flex flex-col gap-3 mt-auto">
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
            {t("wizard.header")}
          </span>
          <span className="text-xs font-medium text-muted-foreground">
            {current} / {total}
          </span>
        </div>
        <div className="flex items-center gap-[3px]">
          {segments.map((segment) => {
            const isPast = segment.state === "past";
            const isCurrent = segment.state === "current";
            const bar = (
              <div
                className={[
                  "w-full h-[3px] rounded-full transition-all duration-300",
                  isCurrent ? "bg-ink-70" : isPast ? "bg-ink-35 group-hover:bg-ink-60" : "bg-ink-10",
                ].join(" ")}
              />
            );

            return (
              <div
                key={segment.id}
                data-testid={`progress-seg-${segment.id}`}
                onClick={isPast ? () => onNavigate(segment.id) : undefined}
                onKeyDown={
                  isPast
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onNavigate(segment.id);
                        }
                      }
                    : undefined
                }
                role={isPast ? "button" : undefined}
                tabIndex={isPast ? 0 : undefined}
                title={isPast ? segment.tooltip : undefined}
                className={[
                  "group flex-1 flex items-center py-2 -my-2",
                  isPast ? "cursor-pointer" : "cursor-default",
                ].join(" ")}
              >
                {bar}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
